import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import {
  Footprints,
  Frown,
  Meh,
  Smile,
  Zap,
  Flame,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Workout, useWorkouts } from "@/hooks/useWorkouts";
import { findGpsCardioExercise } from "@/lib/gpsCardioWorkout";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import { ShareIcon } from "@/components/icons/ShareIcon";
import { useKeyboardHeight } from "@/hooks/useKeyboardHeight";
import { toast } from "sonner";
import { SummaryMapHrCarousel, SummarySplitsSegmentsCarousel } from "@/components/RunSummaryCarousels";
import { RunSummaryExpandedOverlays } from "@/components/RunSummaryExpandedOverlays";
import { SAFE_AREA } from "@/utils/safeArea";
import { buildHrZones, bucketsFromZoneSeconds, type HrZone, type HrZoneBuckets } from "@/lib/heartRateZones";
import { enrichSegmentsWithAvgHr } from "@/lib/runSegmentHr";
import { buildZoneColoredRouteSegments } from "@/lib/routeHrZones";
import { useUnitFormatters } from "@/hooks/useUnits";

const feelingOptions = [
  { id: "exhausted", icon: Frown, label: "Exhausted", color: "text-red-400" },
  { id: "tough", icon: Meh, label: "Tough", color: "text-orange-400" },
  { id: "good", icon: Smile, label: "Good", color: "text-yellow-400" },
  { id: "strong", icon: Zap, label: "Strong", color: "text-primary" },
  { id: "amazing", icon: Flame, label: "Amazing", color: "text-primary" },
] as const;

interface RunHistoryDetailModalProps {
  workout: Workout | null;
  open: boolean;
  onClose: () => void;
}

export const RunHistoryDetailModal = ({
  workout,
  open,
  onClose,
}: RunHistoryDetailModalProps) => {
  const { t } = useTranslation();
  const { formatKm, formatPace, paceSuffix, units } = useUnitFormatters();
  const splitLabel = units === "imperial" ? "mi" : "km";
  const { updateWorkout, deleteWorkout } = useWorkouts();
  const [title, setTitle] = useState("");
  const [feeling, setFeeling] = useState("");
  const [notes, setNotes] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showExpandedMap, setShowExpandedMap] = useState(false);
  const [showExpandedHr, setShowExpandedHr] = useState(false);
  const [showExpandedCombined, setShowExpandedCombined] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);
  const keyboardHeight = useKeyboardHeight();

  // Defer MapKit mount so the sheet can paint / scroll first (avoids WebView crash)
  useEffect(() => {
    if (!open) {
      setMapsReady(false);
      return;
    }
    const t = window.setTimeout(() => setMapsReady(true), 200);
    return () => window.clearTimeout(t);
  }, [open, workout?.id]);

  const scrollInputIntoView = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target;
    const delay = Capacitor.getPlatform() === "ios" ? 450 : 350;
    setTimeout(() => {
      if (document.activeElement === target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, delay);
  }, []);

  // Parse GPS cardio (run / walk / hike) data from exercise notes
  const runningExercise = workout ? findGpsCardioExercise(workout.exercises) : undefined;
  let runningData: any = null;
  if (runningExercise?.notes) {
    try {
      runningData = JSON.parse(runningExercise.notes);
    } catch {}
  }

  const distanceKm =
    runningExercise?.distance_km ||
    Number(runningData?.distance) ||
    0;
  const durationSeconds =
    runningExercise?.duration_seconds ||
    Number(runningData?.durationSeconds) ||
    (workout?.duration_minutes ? Math.round(workout.duration_minutes * 60) : 0);
  const pace = runningData?.pace || "--:--";
  const splits: Array<{ km: number; time: string; pace: string }> =
    runningData?.splits || [];
  const routePoints: Array<{ lat: number; lng: number }> =
    runningData?.routePoints || [];
  const zoneSecondsRaw = runningData?.zoneSeconds as
    | { z1?: number; z2?: number; z3?: number; z4?: number; z5?: number }
    | undefined;
  const caloriesBurned = workout?.calories_burned || 0;
  const heartRateData = runningData?.heartRate as {
    maxHr?: number;
    maxHrSource?: 'measured' | 'estimated';
    targetZone?: string;
    avgBpm?: number | null;
    minBpm?: number | null;
    peakBpm?: number | null;
    zones?: Array<{ id: string; label: string; lowerBpm: number; upperBpm: number }>;
    buckets?: HrZoneBuckets;
    hrSamples?: Array<{ timestamp: number; bpm: number }>;
  } | null;
  const segments: Array<{ index: number; durationSeconds: number; distanceMeters: number; avgHeartRateBpm?: number }> =
    runningData?.segments || [];
  const rawCoachGuideReport = runningData?.coachGuideReport as {
    planText?: string;
    overallSummary?: string;
    overall?: string;
    overallVerdict?: string;
    phases?: Array<{
      label: string;
      kind: string;
      planned: string;
      actual: string;
      verdict: string;
    }>;
    blocks?: Array<{
      label: string;
      kind: string;
      planned: string;
      actual: string;
      verdict: string;
    }>;
  } | undefined;
  const coachGuideReport = rawCoachGuideReport
    ? {
        planText: rawCoachGuideReport.planText,
        overallSummary: rawCoachGuideReport.overallSummary ?? rawCoachGuideReport.overall,
        overallVerdict: rawCoachGuideReport.overallVerdict,
        phases: rawCoachGuideReport.phases ?? rawCoachGuideReport.blocks ?? [],
      }
    : undefined;
  const hrSamples = heartRateData?.hrSamples ?? [];

  const hrZones: HrZone[] = useMemo(() => {
    const base = buildHrZones({ maxHr: heartRateData?.maxHr ?? null });
    if (!heartRateData?.zones?.length) return base.zones;
    return base.zones.map((z) => {
      const saved = heartRateData.zones?.find((s) => s.id === z.id);
      return saved
        ? { ...z, lowerBpm: saved.lowerBpm, upperBpm: saved.upperBpm, label: saved.label }
        : z;
    });
  }, [heartRateData]);

  const coloredRouteSegments = useMemo(() => {
    if (routePoints.length < 2 || hrSamples.length === 0 || hrZones.length === 0) return [];
    const runStartMs = workout?.started_at
      ? new Date(workout.started_at).getTime()
      : (runningData?.startTime ? new Date(runningData.startTime).getTime() : undefined);
    return buildZoneColoredRouteSegments(routePoints, hrSamples, hrZones, {
      runStartMs,
      runDurationSec: durationSeconds > 0 ? durationSeconds : undefined,
    });
  }, [routePoints, hrSamples, hrZones, workout?.started_at, runningData?.startTime, durationSeconds]);

  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const formatSegmentTime = (seconds: number) => formatTime(Math.round(seconds));

  const runSegmentsForCarousel = useMemo(() => {
    const base = segments.map((seg) => ({
      ...seg,
      time: formatSegmentTime(seg.durationSeconds),
    }));
    const startMs = workout?.started_at
      ? new Date(workout.started_at).getTime()
      : (runningData?.startTime ? new Date(runningData.startTime).getTime() : 0);
    if (startMs > 0 && hrSamples.length > 0) {
      return enrichSegmentsWithAvgHr(base, startMs, hrSamples).map((seg) => ({
        ...seg,
        time: formatSegmentTime(seg.durationSeconds),
      }));
    }
    return base;
  }, [segments, workout?.started_at, runningData?.startTime, hrSamples]);

  const mapCenter = useMemo<[number, number] | null>(() => {
    if (routePoints.length === 0) return null;
    const mid = routePoints[Math.floor(routePoints.length / 2)];
    return [mid.lat, mid.lng];
  }, [routePoints]);

  const routeLine = useMemo(
    () => routePoints.map((p) => [p.lat, p.lng] as [number, number]),
    [routePoints],
  );

  const routeSegmentsForMap = useMemo(
    () => (routeLine.length > 0 ? [routeLine] : []) as [number, number][][],
    [routeLine],
  );

  const summaryHeartRate = useMemo(() => {
    let buckets = heartRateData?.buckets as HrZoneBuckets | undefined;
    if (!buckets && zoneSecondsRaw) {
      const built = bucketsFromZoneSeconds(zoneSecondsRaw, hrZones);
      if (built.totalSeconds > 0) buckets = built;
    }
    if (!buckets || hrZones.length === 0) return null;
    return {
      zones: hrZones,
      buckets,
      maxHr: heartRateData?.maxHr ?? hrZones[hrZones.length - 1]?.upperBpm ?? 190,
      maxHrSource: heartRateData?.maxHrSource,
      avgBpm: heartRateData?.avgBpm,
      peakBpm: heartRateData?.peakBpm,
      targetZone: heartRateData?.targetZone as import("@/lib/heartRateZones").HrZoneId | undefined,
      samples: hrSamples,
    };
  }, [heartRateData, hrZones, hrSamples, zoneSecondsRaw]);

  // Initialize editable fields when workout changes
  useEffect(() => {
    if (workout) {
      setTitle(workout.title || "");
      // Extract feeling from difficulty_level (stored as feeling label) or notes
      const savedFeeling = feelingOptions.find(
        (f) => f.label === workout.difficulty_level
      );
      setFeeling(savedFeeling?.id || runningData?.feeling || "");
      // Extract user notes (strip auto-generated feeling/splits lines)
      const rawNotes = workout.notes || "";
      const userNotes = rawNotes
        .split("\n")
        .filter(
          (line) =>
            !line.startsWith("Feeling:") && !line.startsWith("Splits:")
        )
        .join("\n")
        .trim();
      setNotes(userNotes);
    }
  }, [workout, runningData?.feeling]);

  const handleSave = async () => {
    if (!workout?.id) return;
    setSaving(true);
    try {
      const feelingLabel =
        feelingOptions.find((f) => f.id === feeling)?.label || "";
      const workoutNotes = [
        notes.trim(),
        feelingLabel ? `Feeling: ${feelingLabel}` : "",
        splits.length > 0
          ? `Splits: ${splits.map((s) => {
              const parts = s.pace.split(":").map((p) => parseInt(p, 10));
              const paceSec =
                parts.length >= 2 && parts.every((n) => Number.isFinite(n))
                  ? parts[0] * 60 + parts[1]
                  : 0;
              return `${s.km}${splitLabel}: ${formatPace(paceSec)}${paceSuffix}`;
            }).join(", ")}`
          : "",
      ]
        .filter(Boolean)
        .join("\n");

      const { error } = await updateWorkout(workout.id, {
        title: title.trim() || workout.title,
        difficulty_level: feelingLabel || workout.difficulty_level,
        notes: workoutNotes,
      });
      if (error) {
        toast.error(t('components.runHistoryDetail.toast.saveFailed'));
        return;
      }
      toast.success(t('components.runHistoryDetail.toast.runUpdated'));
      onClose();
    } catch {
      toast.error(t('components.runHistoryDetail.toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!workout?.id) return;
    try {
      const { error } = await deleteWorkout(workout.id);
      if (error) {
        toast.error(t('components.runHistoryDetail.toast.deleteFailed'));
        return;
      }
      setShowDeleteConfirm(false);
      toast.success(t('components.runHistoryDetail.toast.runDeleted'));
      onClose();
    } catch {
      toast.error(t('components.runHistoryDetail.toast.deleteFailed'));
    }
  };

  if (!workout) return null;

  const historyDetailSheet = open ? createPortal(
    <div
      className="fixed inset-0 z-[99999] flex flex-col bg-background overflow-hidden"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: `calc(8px + ${SAFE_AREA.top})`,
        paddingBottom: keyboardHeight > 0
          ? `calc(16px + ${keyboardHeight}px + ${SAFE_AREA.bottom})`
          : `calc(16px + ${SAFE_AREA.bottom})`,
        paddingLeft: SAFE_AREA.left,
        paddingRight: SAFE_AREA.right,
        boxSizing: "border-box",
        pointerEvents: "auto",
      }}
    >
      <div className="flex items-center justify-between shrink-0 pb-1 px-1">
        <button
          type="button"
          onClick={onClose}
          className="min-w-[48px] min-h-[48px] flex items-center justify-center text-foreground hover:text-muted-foreground touch-manipulation"
          aria-label={t('common.close')}
        >
          <X className="h-6 w-6" />
        </button>
        <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
          <Footprints className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => setShowShareModal(true)}
            className="min-w-[44px] min-h-[48px] flex items-center justify-center text-primary hover:text-primary/80 touch-manipulation"
          >
            <ShareIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="min-w-[44px] min-h-[48px] flex items-center justify-center text-destructive hover:text-destructive/80 touch-manipulation"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="text-center shrink-0 pb-1">
        <h2 className="text-base font-bold text-center text-foreground">
          {t('components.runHistoryDetail.runDetails')}
        </h2>
        {workout.completed_at && (
          <p className="text-xs text-muted-foreground">
            {new Date(workout.completed_at).toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        )}
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 space-y-3 py-1"
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
      >
        {mapsReady && (routePoints.length > 1 || summaryHeartRate) ? (
          <SummaryMapHrCarousel
            key={workout.id}
            mapCenter={mapCenter}
            routeSegments={routeSegmentsForMap}
            routePoints={routePoints}
            routeLine={routeLine}
            coloredRouteSegments={coloredRouteSegments}
            onExpandMap={() => setShowExpandedMap(true)}
            onExpandHr={() => setShowExpandedHr(true)}
            onExpandCombined={() => setShowExpandedCombined(true)}
            expandMapAria={t("running.summary.expandMap", { defaultValue: "Expand map" })}
            expandHrAria={t("running.summary.expandHr", { defaultValue: "Expand heart rate" })}
            expandCombinedAria={t("running.summary.expandCombined", { defaultValue: "Expand route and heart rate" })}
            heartRate={summaryHeartRate ?? {
              zones: hrZones,
              buckets: {
                secondsInZone: { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 },
                percentInZone: { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 },
                totalSeconds: 0,
              },
              maxHr: hrZones[hrZones.length - 1]?.upperBpm ?? 190,
            }}
          />
        ) : (routePoints.length > 1 || summaryHeartRate) ? (
          <div className="h-40 rounded-lg bg-muted/30 animate-pulse" />
        ) : null}

        <div>
          <label className="text-xs text-muted-foreground mb-0.5 block flex items-center gap-1">
            {t('components.runHistoryDetail.workoutName')} <Pencil className="h-3 w-3" />
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onFocus={scrollInputIntoView}
            className="w-full text-center font-semibold px-2 py-1.5 rounded-md border border-border bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
            placeholder={t('components.runHistoryDetail.runTitlePlaceholder')}
          />
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm bg-muted/30 rounded-lg p-3 border border-border/30">
          <div className="text-center">
            <div className="font-bold text-base text-primary">{formatKm(distanceKm)}</div>
            <div className="text-[10px] text-muted-foreground">{t('components.runHistoryDetail.distance')}</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-base">{formatTime(durationSeconds)}</div>
            <div className="text-[10px] text-muted-foreground">{t('components.runHistoryDetail.totalTime')}</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-base">
              {distanceKm > 0 && durationSeconds > 0
                ? `${formatPace(durationSeconds / distanceKm)}${paceSuffix}`
                : pace}
            </div>
            <div className="text-[10px] text-muted-foreground">{t('components.runHistoryDetail.avgPacePerKm')}</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-base">{caloriesBurned || "--"}</div>
            <div className="text-[10px] text-muted-foreground">{t('components.runHistoryDetail.calories')}</div>
          </div>
        </div>

        {coachGuideReport?.phases?.length ? (
          <div className="chat-selectable rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
            <div className="text-xs font-semibold text-primary">AI Guide — Plan vs Actual</div>
            {coachGuideReport.planText ? (
              <p className="text-[11px] text-muted-foreground leading-snug">
                {coachGuideReport.planText}
              </p>
            ) : null}
            {coachGuideReport.overallSummary ? (
              <p className="text-xs font-medium">{coachGuideReport.overallSummary}</p>
            ) : null}
            <div className="space-y-1.5 max-h-36 overflow-y-auto">
              {coachGuideReport.phases.map((phase) => (
                <div
                  key={`${phase.label}-${phase.kind}`}
                  className="text-[10px] rounded-md bg-background/40 px-2 py-1 border border-border/20"
                >
                  <div className="font-semibold text-foreground">{phase.label}</div>
                  <div className="text-muted-foreground">Plan: {phase.planned}</div>
                  <div className="text-muted-foreground">Actual: {phase.actual}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {(splits.length > 0 || runSegmentsForCarousel.length > 0) && (
          <SummarySplitsSegmentsCarousel kmSplits={splits} runSegments={runSegmentsForCarousel} />
        )}

        <div>
          <label className="text-xs text-muted-foreground mb-1 block">
            {t('components.runHistoryDetail.feelingPrompt')}
          </label>
          <div className="flex justify-between gap-1.5 touch-manipulation">
            {feelingOptions.map((option) => {
              const IconComponent = option.icon;
              const localizedLabel = t(`components.runHistoryDetail.feeling.${option.id}`);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setFeeling(option.id)}
                  className={`flex-1 py-2 rounded-md transition-all flex items-center justify-center ${
                    feeling === option.id
                      ? "bg-primary/20 ring-2 ring-primary scale-110"
                      : "bg-muted/30 hover:bg-muted/50"
                  }`}
                  title={localizedLabel}
                >
                  <IconComponent
                    className={`h-6 w-6 ${feeling === option.id ? option.color : "text-muted-foreground"}`}
                  />
                </button>
              );
            })}
          </div>
          {feeling && (
            <p className="text-xs text-center text-muted-foreground mt-1">
              {t(`components.runHistoryDetail.feeling.${feeling}`)}
            </p>
          )}
        </div>

        <div>
          <label className="text-xs text-muted-foreground mb-1 block">
            {t('components.runHistoryDetail.notes')}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onFocus={scrollInputIntoView}
            className="w-full rounded-md border border-border bg-background/50 p-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            rows={2}
            placeholder={t('components.runHistoryDetail.notesPlaceholder')}
            style={{ scrollMarginBottom: keyboardHeight > 0 ? keyboardHeight + 80 : 100 }}
          />
        </div>

        <div className="flex gap-2 pt-1 pb-2 shrink-0">
          <Button variant="outline" className="flex-1 touch-manipulation min-h-[44px]" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            className="flex-1 bg-gradient-primary touch-manipulation min-h-[44px]"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('components.runHistoryDetail.saving') : t('components.runHistoryDetail.saveChanges')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {historyDetailSheet}

      <RunSummaryExpandedOverlays
        showExpandedMap={showExpandedMap}
        showExpandedHr={showExpandedHr}
        showExpandedCombined={showExpandedCombined}
        onCloseMap={() => setShowExpandedMap(false)}
        onCloseHr={() => setShowExpandedHr(false)}
        onCloseCombined={() => setShowExpandedCombined(false)}
        title={title.trim() || workout.title || t("running.summary.expandedMapFallbackTitle")}
        subtitle={`${formatKm(distanceKm)} • ${formatTime(durationSeconds)} • ${
          distanceKm > 0 ? `${formatPace(durationSeconds / distanceKm)}${paceSuffix}` : pace
        }`}
        routeLine={routeLine}
        routeSegments={routeSegmentsForMap}
        coloredRouteSegments={coloredRouteSegments}
        routePoints={routePoints}
        heartRate={summaryHeartRate}
        closeMapAria={t("running.summary.closeMapAria", { defaultValue: "Close map" })}
        closeHrAria={t("running.summary.closeMapAria", { defaultValue: "Close" })}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
      >
        <AlertDialogContent
          className="bg-background border-border z-[100001]"
          overlayClassName="z-[100001]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('components.runHistoryDetail.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.runHistoryDetail.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('components.runHistoryDetail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal */}
      {showShareModal && workout && (
        <ShareWorkoutModal
          workout={{
            ...workout,
            distance_km: distanceKm,
            average_pace: pace,
            route_coordinates: routePoints,
            duration_seconds: durationSeconds,
            pace_seconds_per_km: distanceKm > 0 ? durationSeconds / distanceKm : 0,
            calories_burned: workout.calories_burned ?? caloriesBurned,
            splits: splits,
            segments: segments.map((seg) => ({
              index: seg.index,
              durationSeconds: seg.durationSeconds,
              distanceMeters: seg.distanceMeters,
              avgHeartRateBpm: seg.avgHeartRateBpm,
            })),
            heart_rate: summaryHeartRate
              ? {
                  zones: summaryHeartRate.zones,
                  buckets: summaryHeartRate.buckets,
                  maxHr: summaryHeartRate.maxHr,
                  avgBpm: summaryHeartRate.avgBpm,
                  peakBpm: summaryHeartRate.peakBpm,
                  targetZone: summaryHeartRate.targetZone,
                }
              : undefined,
          } as any}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
};
